import type { LabeledOption } from '@iheartjane/types';
import { useEffect, useMemo, useRef } from 'react';
import type { ParamParseKey, PathMatch } from 'react-router';
import { matchPath, useMatch, useNavigation } from 'react-router';

import { CategorySelectorSkeleton } from '@jane/bloom/components/skeletonLoaders/categorySelectorSkeleton';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useSkeletonLoader } from '@jane/bloom/hooks/useSkeletonLoader';
import type { SearchFilterFacet } from '@jane/bloom/viewModels/productSearchFilters';

import { CategoryLink } from './categoryLink';
import styles from './categorySelector.module.css';

type CategorySelectorProps = {
  displayCategories: LabeledOption[];
};

// Prefer the in-flight navigation target over the current location so the
// highlight updates optimistically during a category change.
const useMatchesCurrentOrDestinationLocation = <TMatchPattern extends string>(
  matchPattern: TMatchPattern
): PathMatch<ParamParseKey<TMatchPattern>> | null => {
  const { location } = useNavigation();
  const currentPageMatch = useMatch<TMatchPattern>(matchPattern);
  const destinationPageMatch = matchPath<TMatchPattern>(
    matchPattern,
    location?.pathname ?? ''
  );

  return destinationPageMatch || currentPageMatch;
};

export function CategorySelector({ displayCategories }: CategorySelectorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { shouldShowFullSkeletonLoader } = useSkeletonLoader();
  const { shoppingPreferences } = useRootLoaderData();

  const slugCategoryPage = useMatchesCurrentOrDestinationLocation(
    ':storeSlug/:category'
  );
  const localizedCategoryPage = useMatchesCurrentOrDestinationLocation(
    'store/:storeId/:category'
  );
  const categoryPage = useMatchesCurrentOrDestinationLocation(':category');

  const selectedCategory = useMemo(() => {
    return (
      slugCategoryPage?.params.category ??
      localizedCategoryPage?.params.category ??
      categoryPage?.params.category
    );
  }, [categoryPage, localizedCategoryPage, slugCategoryPage]);

  useEffect(() => {
    const scrollContainer = containerRef.current;
    const selectedElement = scrollContainer?.querySelector<HTMLElement>(
      '[data-selected="true"]'
    );

    if (selectedElement && scrollContainer) {
      const { offsetLeft: elementOffsetLeft, offsetWidth: elementWidth } =
        selectedElement;
      const { offsetWidth: containerWidth } = scrollContainer;

      const targetScrollLeft =
        elementOffsetLeft - (containerWidth - elementWidth) / 2;

      scrollContainer.scrollTo({
        behavior: 'smooth',
        left: targetScrollLeft,
      });
    }
  }, []);

  return (
    <nav
      className={styles['categorySelector']}
      id="category-selector"
      ref={containerRef}
    >
      {shouldShowFullSkeletonLoader ? (
        <CategorySelectorSkeleton />
      ) : (
        displayCategories.map((category: SearchFilterFacet) => (
          <CategoryLink
            key={category.value}
            reservationMode={shoppingPreferences?.reservationMode}
            defaultCategory={displayCategories[0].value}
            category={category.value}
            label={category.label}
            selected={
              selectedCategory ===
              (category.value === 'brand_specials'
                ? 'jane-gold'
                : category.value)
            }
          />
        ))
      )}
    </nav>
  );
}
